import React, { useEffect, useRef, useState } from 'react';
import { getMinimumQuantity, getQuantities } from '../../../../price/selectors';
import { getQuantity, getQuantityConfig } from '../../../../bom/selectors';
import { useDispatch, useSelector } from 'react-redux';

import { BomOptionsStepSeparateQuantity } from '../../../../bom/constants';
import CustomQuantity from '../CustomQuantity';
import FeedbackWrapper from '../../../common/FeedbackWrapper';
import Form from 'react-bootstrap/Form';
import PropTypes from 'prop-types';
import SelectWrapper from '../SelectWrapper';
import StepNumber from '../StepNumber';
import { components } from 'react-select';
import { formatQuantity } from '../../../../utils/formatter';
import { saveQuantity } from '../../../../bom/actions';

const CustomMenuList = ({ selectProps, ...props }) => {
  const { menuInputValue, onAcceptMenuInputValue, onInputChange, onMenuInputFocus } = selectProps;

  let quantity = Number(menuInputValue);
  if (isNaN(quantity)) quantity = 0;

  // Copied from source
  const ariaAttributes = {
    'aria-autocomplete': 'list',
    'aria-label': selectProps['aria-label'],
    'aria-labelledby': selectProps['aria-labelledby'],
  };

  const handleCustomQuantityChange = (qty) => {
    onInputChange(qty, {
      action: 'input-change',
    });
  };

  return (
    <>
      <CustomQuantity
        {...ariaAttributes}
        quantity={quantity}
        onAcceptQuantity={onAcceptMenuInputValue}
        onChange={handleCustomQuantityChange}
        onFocus={onMenuInputFocus}
      />
      <components.MenuList {...props} selectProps={selectProps} />
    </>
  );
};

const CustomValueContainer = ({ children, ...props }) => (
  <components.ValueContainer {...props}>
    {children}
    {props.hasValue ? (
      <components.SingleValue {...props}>
        {props.selectProps.getOptionLabel(props.selectProps.value)}
      </components.SingleValue>
    ) : (
      <components.Placeholder {...props}>{props.selectProps.placeholder}</components.Placeholder>
    )}
  </components.ValueContainer>
);

const LockedQuantityDropdown = ({ feedback, resetFeedback }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const originalQuantity = useSelector(getQuantity);
  const quantities = useSelector(getQuantities);
  const config = useSelector(getQuantityConfig);
  const miniQty = useSelector(getMinimumQuantity);

  const [focused, setFocused] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [selectedOption, setSelectedOption] = useState(
    originalQuantity > 0
      ? {
          label: formatQuantity(originalQuantity),
          value: originalQuantity,
        }
      : null
  );

  const options = quantities.map((quantity) => ({
    label: formatQuantity(quantity),
    value: quantity,
  }));

  useEffect(() => {
    const collapseDropdown = (e) => {
      if (!!containerRef && !!containerRef.current) {
        const menu = containerRef.current.querySelector('.select__menu');

        if (!containerRef.current.contains(e.target) || !menu || !menu.contains(e.target)) {
          setTimeout(() => {
            setFocused(false);
            setQuantity(0);
          }, 20);
        }
      }
    };

    document.addEventListener('mousedown', collapseDropdown);

    return () => {
      document.removeEventListener('mousedown', collapseDropdown);
    };
  }, []);

  const handleAcceptMenuInputValue = () => {
    if (quantity > 0 && quantity !== originalQuantity) {
      setSelectedOption({
        label: formatQuantity(quantity),
        value: quantity,
      });
      dispatch(saveQuantity(quantity));
      resetFeedback('quantity');
    }
  };

  const handleChange = (option) => {
    if (!selectedOption || (!!selectedOption && selectedOption.value !== option.value)) {
      setSelectedOption(option);
      dispatch(saveQuantity(option.value));
      resetFeedback('quantity');
    }
    setFocused(false);
    setQuantity(0);
  };

  const handleInputChange = (qty) => {
    setQuantity(qty);
  };

  const handleMenuInputFocus = () => {
    setFocused(true);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label id="locked-quantity-dropdown-label">
        <StepNumber step={BomOptionsStepSeparateQuantity} />
        Quantity:
      </Form.Label>
      <div ref={containerRef}>
        <SelectWrapper
          {...{
            isFocused: focused || undefined,
            menuIsOpen: focused || undefined,
          }}
          aria-labelledby="locked-quantity-dropdown-label"
          classNamePrefix="select"
          components={{
            MenuList: CustomMenuList,
            ValueContainer: CustomValueContainer,
          }}
          filterOption={() => true}
          id="locked-quantity-dropdown"
          isInvalid={!!feedback['quantity']}
          isSearchable={false}
          menuInputValue={quantity}
          options={options}
          placeholder="Select quantity"
          value={selectedOption}
          onAcceptMenuInputValue={handleAcceptMenuInputValue}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onMenuInputFocus={handleMenuInputFocus}
        />
      </div>
      <Form.Text muted>
        {`Can only be ordered in groups of ${formatQuantity(
          config.minimumDivisor
        )}. Minimum quantity is ${formatQuantity(miniQty)}`}
      </Form.Text>
      {!!feedback['quantity'] && <FeedbackWrapper>{feedback['quantity']}</FeedbackWrapper>}
    </Form.Group>
  );
};

LockedQuantityDropdown.propTypes = {
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  resetFeedback: PropTypes.func.isRequired,
};

export default LockedQuantityDropdown;
